import { View, Text, styled, ScrollView } from 'tamagui'
import { useState } from 'react'
import { Check, Headphones, Video, Upload, UserCircle, Zap, Sparkles, Shield } from '@tamagui/lucide-icons'
import { users, servers } from '@/mocks'
import { TIER_CONFIGS } from '@/mocks'
import { ProBadge, TierBadge } from '../../common'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 24, maxWidth: 600 })

const Card = styled(View, {
  backgroundColor: '$surfaceRaised', borderRadius: '$borderRadiusXl', borderWidth: 1, borderColor: '$borderDefault', padding: 16, gap: 12,
})

const SectionTitle = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
})

const Label = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground',
})

const Muted = styled(Text, {
  fontFamily: '$fontBody', fontSize: 13, color: '$sidebarMuted',
})

const SmallMuted = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, color: '$color8',
})

const ActionButton = styled(View, {
  height: 36, paddingHorizontal: 20, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center', backgroundColor: '$accent',
  cursor: 'pointer', alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const PerkRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', gap: 10, height: 32,
})

const ToggleContainer = styled(View, {
  flexDirection: 'row', borderRadius: '$borderRadiusLg', borderWidth: 1, borderColor: '$borderDefault',
  overflow: 'hidden', alignSelf: 'flex-start',
})

const ToggleOption = styled(View, {
  paddingHorizontal: 16, paddingVertical: 8, cursor: 'pointer',
  variants: {
    active: {
      true: { backgroundColor: '$accent' },
      false: { backgroundColor: 'transparent' },
    },
  } as const,
})

const perks = [
  { icon: Headphones, label: 'HD audio quality everywhere (Opus at higher bitrate)' },
  { icon: Video, label: '1080p video in servers that support 720p' },
  { icon: Upload, label: '10 GB file upload limit (vs 500 MB free)' },
  { icon: UserCircle, label: 'Extended profile customization (bio, banner, animated avatar)' },
  { icon: Zap, label: 'Priority voice routing (lower latency connections)' },
  { icon: Sparkles, label: 'Early access to new features' },
  { icon: Shield, label: 'Pro badge on profile' },
]

export function SubscriptionSettings() {
  const user = users['u1']
  const isPro = user.isPro
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')

  const userServers = Object.values(servers)

  return (
    <Container>
      <Header><Title>Subscription</Title></Header>
      <ScrollView flex={1}>
        <Body>
          {/* Current Plan Card */}
          {isPro ? (
            <Card borderColor="$accent">
              <View flexDirection="row" alignItems="center" gap={10}>
                <ProBadge />
                <Label fontWeight="600" fontSize={16}>Arcus Pro</Label>
              </View>
              <Label fontWeight="600" fontSize={20}>$5/month</Label>
              <Muted>Renews April 1, 2026</Muted>
              <ActionButton>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">
                  Manage Subscription
                </Text>
              </ActionButton>
            </Card>
          ) : (
            <Card>
              <View flexDirection="row" alignItems="center" gap={10}>
                <Label fontWeight="600" fontSize={16} color="$sidebarMuted">Free Plan</Label>
              </View>
              <Muted>Upgrade to Pro to unlock premium features across all servers.</Muted>
              <ActionButton>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">
                  Upgrade to Pro
                </Text>
              </ActionButton>
            </Card>
          )}

          {/* Pro Perks List */}
          <View gap={8} {...(!isPro && { opacity: 0.6 })}>
            <SectionTitle>Pro Perks</SectionTitle>
            {perks.map((perk) => {
              const Icon = perk.icon
              return (
                <PerkRow key={perk.label}>
                  <View width={20} height={20} alignItems="center" justifyContent="center">
                    {isPro ? (
                      <Check size={16} color="$accent" />
                    ) : (
                      <Icon size={16} color="$sidebarMuted" />
                    )}
                  </View>
                  <Label fontSize={13}>{perk.label}</Label>
                </PerkRow>
              )
            })}
          </View>

          {/* Pricing Toggle */}
          <Card>
            <SectionTitle>Pricing</SectionTitle>
            <ToggleContainer>
              <ToggleOption
                active={billingCycle === 'monthly'}
                onPress={() => setBillingCycle('monthly')}
              >
                <Text
                  fontFamily="$fontBody"
                  fontSize={13}
                  fontWeight="600"
                  color={billingCycle === 'monthly' ? '$surface' : '$sidebarMuted'}
                >
                  $5/month
                </Text>
              </ToggleOption>
              <ToggleOption
                active={billingCycle === 'annual'}
                onPress={() => setBillingCycle('annual')}
              >
                <View flexDirection="row" alignItems="center" gap={6}>
                  <Text
                    fontFamily="$fontBody"
                    fontSize={13}
                    fontWeight="600"
                    color={billingCycle === 'annual' ? '$surface' : '$sidebarMuted'}
                  >
                    $48/year
                  </Text>
                  <View backgroundColor="#E8A840" borderRadius="$borderRadiusSm" paddingHorizontal={5} paddingVertical={1}>
                    <Text fontFamily="$fontBody" fontSize={9} fontWeight="700" color="$surface">
                      SAVE 20%
                    </Text>
                  </View>
                </View>
              </ToggleOption>
            </ToggleContainer>
            <Muted>
              {billingCycle === 'monthly' ? '$5 billed monthly' : '$48 billed annually ($4/mo effective)'}
            </Muted>
          </Card>

          {/* Server Benefits Callout */}
          <Card>
            <SectionTitle>Your Servers</SectionTitle>
            <SmallMuted>Pro benefits apply across all your servers.</SmallMuted>
            <View gap={8} marginTop={4}>
              {userServers.map((s) => (
                <View key={s.id} flexDirection="row" alignItems="center" gap={10}>
                  <View
                    width={28} height={28} borderRadius="$borderRadiusLg"
                    backgroundColor={s.iconColor}
                    alignItems="center" justifyContent="center"
                  >
                    <Text fontFamily="$fontHeading" fontWeight="600" fontSize={11} color="$surface">
                      {s.iconText}
                    </Text>
                  </View>
                  <Label fontSize={13} flex={1}>{s.name}</Label>
                  <TierBadge tier={s.tier} size="sm" />
                </View>
              ))}
            </View>
          </Card>
        </Body>
      </ScrollView>
    </Container>
  )
}
